import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Accordion, RadioGroup, InputField } from '@ets/design-system';

import { parseMeasurement } from 'utils';

import { MeasureInput } from '_common/components';
import { convertToDataUnit, convertToDisplayUnit } from '../utils';

import styles from './Section.module.scss';

type TableAlignmentProps = Pick<Editor.Edition.TableProperties, 'alignment' | 'leftIndentation'> &
  SectionProps;

const DEFAULT_PROPERTIES: Pick<Editor.Edition.TableProperties, 'alignment' | 'leftIndentation'> = {
  alignment: 'l',
  leftIndentation: 0,
};

const TableAlignment = ({ alignment, leftIndentation, updateData }: TableAlignmentProps) => {
  const intl = useIntl();

  const alignmentOptions = useMemo<
    { label: string; value: Editor.Edition.TableProperties['alignment'] }[]
  >(
    () => [
      { label: intl.formatMessage({ id: 'LEFT' }), value: 'l' },
      { label: intl.formatMessage({ id: 'CENTER' }), value: 'c' },
      { label: intl.formatMessage({ id: 'RIGHT' }), value: 'r' },
    ],
    [],
  );

  const [localAlignment, setLocalAlignment] = useState(
    alignmentOptions.find((option) => option.value === (alignment ?? DEFAULT_PROPERTIES.alignment)),
  );
  const [localLeftIndent, setLocalLeftIndent] = useState(
    convertToDisplayUnit(leftIndentation ?? DEFAULT_PROPERTIES.leftIndentation),
  );

  const handleTableAlignmentChange = (
    newAlignment: Editor.Edition.TableProperties['alignment'],
  ) => {
    if (newAlignment === localAlignment?.value) {
      return;
    }

    setLocalAlignment(alignmentOptions.find((option) => option.value === newAlignment));
    updateData({ property: 'alignment', value: newAlignment });
  };

  const handleLeftIndentChange = (newValue: string) => {
    const normalizedIndent =
      parseMeasurement(newValue, 'cm', {
        defaultUnit: 'cm',
      }) ?? 0;

    setLocalLeftIndent(normalizedIndent);
    updateData({ property: 'leftIndentation', value: convertToDataUnit(normalizedIndent) });
  };

  return (
    <Accordion
      size="m"
      variant="standard"
      title={intl.formatMessage({ id: 'TABLE_ALIGNMENT' })}
      defaultOpen={true}
      testId="tableProperties-table-alignment"
    >
      <div className={styles.root}>
        <div className={`${styles.column} ${styles.radioGroup}`}>
          <RadioGroup
            items={alignmentOptions.map(({ label, value }) => ({ label, value: value ?? 'l' }))}
            value={localAlignment?.value}
            onChange={(e) => handleTableAlignmentChange(e.target.value)}
            testId="tableProperties-alignment"
            direction="vertical"
            size="xs"
          />
        </div>
        <div className={styles.column}>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'LEFT_INDENT' })}
            testId="tableProperties-left-ident"
          >
            <div className={styles.row}>
              <MeasureInput
                size="m"
                value={`${localLeftIndent}`}
                onChange={handleLeftIndentChange}
                placeholder=""
                valueSuffix="cm"
                avoidEmpty
                testId="tableProperties-left-ident"
              />
            </div>
          </InputField>
        </div>
      </div>
    </Accordion>
  );
};

export default TableAlignment;
